/*
  Kopstorm — Currency code guard (v3)
  ------------------------------------------------------------------
  Shows the ISO currency code after prices ONLY for CAD, AUD and NZD
  markets, so customers do not mistake local dollar prices for USD.
  For all other markets (US, UK, EU, ...) any trailing currency code
  is stripped.

  Division of labor:
  - Theme elements (PDP price, ATC buttons, sticky ATC, cart drawer):
    this guard appends the code on CAD/AUD/NZD and strips elsewhere.
  - Kaching bundle widget (the deal bars): Kaching renders the
    currency code itself on multi-currency markets, so this guard
    never appends inside the widget root. It only removes duplicate
    codes there and strips codes on non-code markets such as US/UK.
    Kaching's native price rewrites OUTSIDE the widget (theme ATC
    button after selecting a bundle) do NOT include the code, so the
    guard does append there.

  Runs as a MutationObserver so it keeps prices correct after any
  client side rewrite (Kaching updating the ATC button, theme variant
  and quantity updates, cart drawer re-renders).

  Keep the currency list in sync with snippets/kopstorm-money.liquid
*/
(function () {
  'use strict';

  var CODE_CURRENCIES = ['CAD', 'AUD', 'NZD'];
  var KNOWN_CODES = 'CAD|AUD|NZD|USD|GBP|EUR';

  // Only the bundle widget itself is append-exempt. Kaching also
  // injects elements with kaching classes elsewhere (for example the
  // native price inside the theme ATC button when a bundle is
  // selected) and THOSE need the code appended by this guard.
  var KACHING_SCOPE = 'kaching-bundles-block,.kaching-bundles';

  // Containers whose text may hold prices.
  var TARGET_SELECTORS = [
    '.price-item',
    '.main-atc-price',
    '.main-atc-compare-price',
    '[class*="main-atc-price-span"]',
    '.price--end',
    '.cart-item__final-price',
    '.cart-item__old-price',
    '.cart-drawer__totals__row__money',
    '.cs_upsell__price',
    '.cs_upsell__price--compare',
    'kaching-bundles-block',
    '[id*="kaching"]',
    '[class*="kaching"]'
  ].join(',');

  function getActiveCurrency() {
    if (window.Shopify && Shopify.currency && Shopify.currency.active) {
      return String(Shopify.currency.active).toUpperCase();
    }
    var script = document.querySelector('script[data-kopstorm-currency]');
    if (script) {
      return String(script.getAttribute('data-kopstorm-currency') || '').toUpperCase();
    }
    return '';
  }

  var activeCurrency = getActiveCurrency();
  if (!activeCurrency) return;

  var shouldShowCode = CODE_CURRENCIES.indexOf(activeCurrency) !== -1;

  var CODE_WORD_RE = new RegExp('\\b(?:' + KNOWN_CODES + ')\\b', 'g');
  var SPACE_CODE_RE = new RegExp('\\s*\\b(?:' + KNOWN_CODES + ')\\b', 'g');
  var REPEAT_CODE_RE = new RegExp(
    '\\b(' + KNOWN_CODES + ')\\b(?:\\s+(?:' + KNOWN_CODES + ')\\b)+',
    'g'
  );
  // "$44.95", "CA$89.90", "A$5", "NZ$1,234.50"
  var AMOUNT_RE = /((?:US|CA|A|NZ)?\$\s?\d(?:[\d.,]*\d)?)/g;

  function codeCount(text) {
    var matches = text.match(CODE_WORD_RE);
    return matches ? matches.length : 0;
  }

  function fixTextNode(node) {
    var text = node.nodeValue;
    var parent = node.parentElement;
    if (!text || !parent) return;

    var updated = text;

    if (!shouldShowCode) {
      // US, UK, EU, ...: no code anywhere, including Kaching's own.
      updated = updated.replace(SPACE_CODE_RE, '');
    } else {
      var insideKaching = parent.closest(KACHING_SCOPE) !== null;
      var ownCodes = codeCount(text);
      var parentCodes = codeCount(parent.textContent);

      if (ownCodes > 0) {
        if (parentCodes - ownCodes >= 1) {
          // Another node in this parent already carries a code: this
          // one is a duplicate. Removing it self-heals doubled codes.
          updated = updated.replace(SPACE_CODE_RE, '');
        } else if (ownCodes > 1) {
          // Repeated code within one node: collapse to a single one.
          updated = updated.replace(REPEAT_CODE_RE, '$1');
        } else if (!insideKaching) {
          // Theme element with a wrong code: normalize to active.
          updated = updated.replace(CODE_WORD_RE, activeCurrency);
        }
      } else if (
        !insideKaching &&
        parentCodes === 0 &&
        updated.indexOf('$') !== -1
      ) {
        // Theme element without any code: append it. Kaching widgets
        // are exempt because Kaching renders the code itself.
        updated = updated.replace(AMOUNT_RE, '$1 ' + activeCurrency);
      }
    }

    if (updated !== text) {
      node.nodeValue = updated;
    }
  }

  function processAll() {
    var targets = document.querySelectorAll(TARGET_SELECTORS);
    for (var i = 0; i < targets.length; i++) {
      var walker = document.createTreeWalker(targets[i], NodeFilter.SHOW_TEXT, null);
      var node;
      while ((node = walker.nextNode())) {
        var parent = node.parentNode;
        if (parent && (parent.nodeName === 'SCRIPT' || parent.nodeName === 'STYLE')) {
          continue;
        }
        fixTextNode(node);
      }
    }
  }

  var scheduled = false;
  function schedulePass() {
    if (scheduled) return;
    scheduled = true;
    window.requestAnimationFrame(function () {
      scheduled = false;
      processAll();
    });
  }

  function init() {
    processAll();
    // Our own text fixes are idempotent, so passes triggered by our
    // mutations make no further changes and the observer settles.
    var observer = new MutationObserver(schedulePass);
    observer.observe(document.body, {
      subtree: true,
      childList: true,
      characterData: true
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();